import { useDl } from "../utils/dlToken";
import { useState, useEffect, useCallback } from "react";
import { previewDoc } from "../utils/previewDoc";
import axios from "axios";
import {
  RefreshCw, Trash2, Mail, Building2, User, BadgeCheck, CheckCircle2,
  Edit3, Save, X, Phone, MapPin, Eye, Video, FileText, Paperclip, FileSpreadsheet,
  ArrowDownLeft, ArrowUpRight, MessagesSquare, Send, Loader2, Download, Signature, Printer, ClipboardCheck,
  Trophy, ExternalLink, Award, ChevronDown, ChevronUp, ArrowRightLeft, Clock, Search,
} from "lucide-react";
import { openPdf } from "../utils/pdf";
import { EmailLangPicker, pickEmailLang } from "./EmailLangPicker";
import { profSummary } from "../content/professions";
import ProfessionFilter from "./ProfessionFilter";
import AdminPager, { usePagedList, StateChips } from "./AdminPager";
import PromoToggle from "./PromoToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const CORR_TYPE_LABEL = {
  application: "Podaná žiadosť",
  contact_message: "Správa (formulár)",
  contact_reply: "Automatická odpoveď",
  acceptance: "Potvrdenie prijatia",
  docs_received: "Podklady prijaté",
  invoice: "Faktúra",
  admin_message: "E-mail od nás",
};

const STATUS_LABEL = {
  submitted: "Podaná",
  paid: "Uhradená",
  approved: "Schválená",
  suspended: "Pozastavená",
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("sk-SK", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const EDIT_FIELDS = [
  ["subject_name", "Názov / Meno"],
  ["ico_birth", "IČO / Dátum narodenia"],
  ["address", "Sídlo / Adresa"],
  ["responsible_person", "Zodpovedná osoba"],
  ["phone", "Telefón"],
  ["specialization", "Špecializácia"],
  ["workers_count", "Počet pracovníkov"],
  ["equipment", "Vybavenie"],
  ["certificates", "Certifikáty"],
];

export default function AdminApplications({ token }) {
  const [profF, setProfF] = useState([]);
  const [q, setQ] = useState("");
  const [st, setSt] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const list = usePagedList(`${API}/admin/applications`, token, { q, profs: profF.join(","), state: st });
  const sc = list.state_counts || {};
  const stateOpts = [["", "Všetky", list.all_total], ["draft", "Rozpracované (neodoslané)"], ["new", "Nové – čakajú na schválenie"], ["approved_unpaid", "Schválené – nezaplatené"],
    ["verifying", "Prebieha overovanie"], ["certified", "Certifikované"], ["suspended", "Pozastavené"]]
    .map(([key, label, c]) => ({ key, label, count: key ? sc[key] : c }));
  const { items: apps, loading, reload: fetchApps } = list;

  const approve = async (id, feeWaived = false, discount = 0) => {
    const msg = feeWaived
      ? "Schváliť žiadosť BEZ registračného poplatku? Pridelí sa číslo, uchádzač dostane oznámenie s textom „Registračný poplatok odpustený“ (bez QR platby a bez faktúry) a môže sa začať overovanie."
      : discount
        ? `Schváliť žiadosť so ZĽAVOU ${discount} %? Pridelí sa číslo a uchádzačovi sa odošle oznámenie s pôvodnou cenou, zľavou a QR kódmi na zľavnenú sumu.`
        : "Schváliť žiadosť S registračným poplatkom? Pridelí sa číslo a uchádzačovi sa odošle oznámenie s QR kódmi na úhradu.";
    if (!window.confirm(msg)) return;
    try { await axios.post(`${API}/admin/applications/${id}/approve`, { fee_waived: feeWaived, discount }, auth); fetchApps(); }
    catch (e) { alert("Chyba pri schvaľovaní."); }
  };

  const togglePaid = async (app) => {
    try { await axios.put(`${API}/admin/applications/${app.id}`, { paid: !app.paid }, auth); fetchApps(); }
    catch (e) { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Naozaj NATRVALO zmazať tohto žiadateľa? Zmaže sa celý účet vrátane žiadosti, nahratých súborov, platieb, korešpondencie, položiek „Na podpis“ a podpísaných dokladov. Zápis v Sieni slávy (Tigik) treba odstrániť ručne. E-mail sa bude dať znovu zaregistrovať.")) return;
    try { await axios.delete(`${API}/admin/applications/${id}`, auth); fetchApps(); }
    catch (e) { /* ignore */ }
  };

  const saveEdit = async (id, fields) => {
    try { await axios.put(`${API}/admin/applications/${id}`, fields, auth); fetchApps(); return true; }
    catch (e) { alert("Chyba pri ukladaní."); return false; }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/applications.csv`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "ziadosti.csv"; a.click();
    } catch { alert("Export zlyhal."); }
  };

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <p className="text-white/40 text-sm" data-testid="apps-count">{list.all_total ?? list.total} žiadostí o certifikáciu{(q || profF.length || st) ? ` · nájdených ${list.total}` : ""}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCsv} data-testid="apps-export-csv"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <FileSpreadsheet size={15} /> Export CSV
          </button>
          <button onClick={fetchApps} data-testid="apps-refresh"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Obnoviť
          </button>
        </div>
      </div>
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="apps-search"
          placeholder="Hľadať podľa mena, e-mailu, IČO alebo evidenčného čísla…"
          className="w-full bg-[#0a0a0a] border border-white/15 pl-10 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
      </div>
      <PromoToggle token={token} />
      <StateChips options={stateOpts} value={st} onChange={setSt} testid="apps-state" />
      <ProfessionFilter value={profF} onChange={setProfF} testid="apps-prof-filter" />
      {apps.length === 0 ? (
        <p className="text-white/40 text-center py-16" data-testid="apps-empty">{loading ? "Načítavam…" : (q || profF.length || st) ? "Žiadna žiadosť nezodpovedá hľadaniu." : "Zatiaľ žiadne žiadosti o certifikáciu."}</p>
      ) : (
        <div className="space-y-4">
          {apps.map((a) => (
            <AppCard key={a.id} a={a} token={token} onApprove={approve} onTogglePaid={togglePaid} onRemove={remove} onSave={saveEdit} />
          ))}
        </div>
      )}
      <AdminPager page={list.page} pages={list.pages} total={list.total} onPage={list.setPage} testid="apps-pager" />
    </div>
  );
}

function AppCard({ a, token, onApprove, onTogglePaid, onRemove, onSave }) {
  const dl = useDl(token);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [files, setFiles] = useState([]);
  const [corr, setCorr] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [composing, setComposing] = useState(false);
  const [docs, setDocs] = useState([]);
  const [signedDocs, setSignedDocs] = useState([]);
  const [unseen, setUnseen] = useState(a.signed_unseen || 0);
  const [letterModal, setLetterModal] = useState(null); // letter type
  const [auditConfirmed, setAuditConfirmed] = useState(a.audit_confirmed || false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [hof, setHof] = useState(a.tigik_id ? { ok: true, id: a.tigik_id, public_url: a.tigik_public_url, action: a.tigik_status, welcome_email_queued: a.tigik_welcome_email_queued, env: a.tigik_env } : null);
  const [hofBusy, setHofBusy] = useState(false);
  const [certified, setCertified] = useState(a.certified || false);
  const [certBusy, setCertBusy] = useState(false);
  const [certMsg, setCertMsg] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadSigned = useCallback(() => {
    axios.get(`${API}/admin/applicants/${a.applicant_id}/signed-docs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setSignedDocs(data)).catch(() => setSignedDocs([]));
  }, [a.applicant_id, token]);

  const loadDocs = useCallback(() => {
    axios.get(`${API}/admin/applications/${a.id}/documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setDocs(data)).catch(() => setDocs([]));
  }, [a.id, token]);

  const loadCorr = useCallback(() => {
    axios.get(`${API}/admin/correspondence`, { params: { email: a.applicant_email, applicant_id: a.applicant_id }, ...auth })
      .then(({ data }) => setCorr(data)).catch(() => setCorr([]));
  }, [a.applicant_email, a.applicant_id, token]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/applicants/${a.applicant_id}/files`, auth)
      .then(({ data }) => setFiles(data)).catch(() => setFiles([]));
    loadCorr();
    loadDocs();
    loadSigned();
    if (a.signed_unseen > 0) {
      axios.post(`${API}/admin/applicants/${a.applicant_id}/signed-docs/mark-seen`, {}, auth)
        .then(() => setUnseen(0)).catch(() => {});
    }
  }, [open, a.applicant_id, a.applicant_email, token, loadCorr, loadDocs, loadSigned]);

  const viewFile = (id) => openPdf(`${API}/files/${id}/download?token=${dl}`, { filename: "dokument" });

  const openAuditProtocol = () =>
    openPdf(`${API}/admin/applications/${a.id}/audit-protocol?token=${dl}`, { filename: `audit-protokol-${a.evidence_number || a.id}.pdf` });
  const downloadBackup = () =>
    window.open(`${API}/admin/applications/${a.id}/backup.zip?token=${dl}`, "_blank");
  const confirmAudit = async () => {
    setAuditBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/applications/${a.id}/confirm-audit`, {}, auth);
      setAuditConfirmed(data.audit_confirmed);
    } catch (e) { /* noop */ } finally { setAuditBusy(false); }
  };

  const confirmCertified = async () => {
    setCertBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/applications/${a.id}/confirm-certified`, {}, auth);
      setCertified(data.certified);
      if (data.certified) setCertMsg(data.email_sent ? "✓ E-mail o vydaní certifikátu odoslaný uchádzačovi (doručenie do 14 dní)." : "Certifikát potvrdený, no e-mail sa nepodarilo odoslať.");
      else setCertMsg("");
    } catch (e) { /* noop */ } finally { setCertBusy(false); }
  };

  const inductHof = async () => {
    setHofBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/applications/${a.id}/hof-induct`, {}, auth);
      setHof(data);
    } catch (e) {
      setHof({ ok: false, error: e?.response?.data?.detail || "Uvedenie do Siene slávy zlyhalo." });
    } finally { setHofBusy(false); }
  };

  const startEdit = () => {
    const d = {};
    EDIT_FIELDS.forEach(([k]) => { d[k] = a[k] || ""; });
    setDraft(d); setEditing(true); setOpen(true);
  };
  const save = async () => { if (await onSave(a.id, draft)) setEditing(false); };

  const [flowMsg, setFlowMsg] = useState("");
  const [disc, setDisc] = useState(0);
  const [flow, setFlow] = useState({ paid: !!a.paid, ok: !!a.verification_success_at });
  useEffect(() => { setFlow({ paid: !!a.paid, ok: !!a.verification_success_at }); }, [a.paid, a.verification_success_at]);
  const [fin, setFin] = useState({ amount: a.final_fee_amount, paid: !!a.final_paid, promo: !!a.final_fee_promo });
  useEffect(() => { setFin({ amount: a.final_fee_amount, paid: !!a.final_paid, promo: !!a.final_fee_promo }); }, [a.final_fee_amount, a.final_paid, a.final_fee_promo]);
  const flowAction = async (path, ask, done) => {
    if (!window.confirm(ask)) return;
    try {
      const { data } = await axios.post(`${API}/admin/applications/${a.id}/${path}`, {}, auth);
      setFlow({ paid: !!data.paid, ok: !!data.verification_success_at }); setFlowMsg(done);
      setFin({ amount: data.final_fee_amount, paid: !!data.final_paid, promo: !!data.final_fee_promo });
    } catch (e) { setFlowMsg(e?.response?.data?.detail || "Akcia zlyhala."); }
  };

  const approved = a.status === "approved";

  return (
    <>
    <div data-testid="application-card" className="border border-white/10 bg-[#0a0a0a] hover:border-white/20 transition-colors">
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            {a.type === "company" ? <Building2 className="text-[#D4AF37] mt-1" size={20} /> : <User className="text-[#D4AF37] mt-1" size={20} />}
            <div>
              <p className="font-display text-lg leading-tight">{a.subject_name || a.applicant_name}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-white/55">
                <button type="button" onClick={() => { try { navigator.clipboard.writeText(a.applicant_email); } catch (e) {} }} title="Kliknutím skopírujete e-mail (neotvára osobný e-mail)" className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] cursor-pointer bg-transparent border-0 p-0 text-inherit"><Mail size={13} /> {a.applicant_email}</button>
                {a.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {a.phone}</span>}
                <span className="uppercase text-xs">{a.type === "company" ? "Firma" : "Jednotlivec"}</span>
                {a.type_switched_at && (
                  <span data-testid="type-switched-note" title="Uchádzač si sám opravil typ účtu pred schválením"
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 border border-amber-500/50 text-amber-300 bg-amber-500/10">
                    <ArrowRightLeft size={12} /> Typ zmenený: {a.previous_type === "company" ? "Firma" : "Jednotlivec"} → {a.type === "company" ? "Firma" : "Jednotlivec"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {unseen > 0 && (
              <span data-testid="signed-unseen-badge" title="Nové podpísané dokumenty čakajú na spracovanie"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-amber-500/50 text-amber-300 bg-amber-500/10 animate-pulse">
                <Signature size={12} /> Nové podpisy · {unseen}
              </span>
            )}
            <span className={`text-xs px-2.5 py-1 border ${a.status === "suspended" ? "border-red-500/60 text-red-400" : approved ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-white/60"}`}>{STATUS_LABEL[a.status] || a.status}</span>
            <span data-testid="app-paid-toggle" onClick={() => onTogglePaid(a)} role="button"
              className={`cursor-pointer text-xs px-2.5 py-1 border ${a.paid ? "border-green-500/50 text-green-400" : "border-white/15 text-white/40"}`}>
              {a.fee_waived ? "✓ Poplatok odpustený" : a.paid ? "✓ Zaplatené" : "Nezaplatené"}{a.fee_discount ? ` · zľava ${a.fee_discount} % (${Number(a.fee_amount).toFixed(2).replace(".", ",")} €)` : ""}
            </span>
            <span data-testid="app-certified-toggle" onClick={confirmCertified} role="button"
              title={certified ? "Certifikát vydaný — kliknutím zrušíte" : "Označiť ako certifikovaný (certifikát vydaný)"}
              className={`cursor-pointer inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border transition-colors ${certified ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10" : "border-white/15 text-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
              <Award size={13} /> {certified ? "Certifikovaný ✓" : "Certifikovaný?"}
            </span>
          </div>
        </div>

        {a.type !== "company" && (
          <div className="mt-3 mr-4 inline-flex items-center text-xs px-2.5 py-1 border border-[#D4AF37]/40 text-[#F2E3C6]" data-testid="application-track">
            {a.track === "verification" ? "Žiada o preverenie online Sa.Eko.Verified" : "Žiada o zlatý certifikát Sa.Eko.Prs."}
          </div>
        )}
        {profSummary(a) && (
          <div className="mt-3 mr-4 inline-flex items-center text-xs px-2.5 py-1 border border-white/15 text-white/70" data-testid="application-professions">
            {profSummary(a)}
          </div>
        )}
        {a.evidence_number && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="application-flow">
            {!flow.paid && (
              <button onClick={() => flowAction("bank-paid", `Potvrdiť prijatú úhradu ${a.fee_amount || (a.type === "company" ? 490 : 59)} € na účet? Vystaví sa faktúra a začne overovanie.`, "Úhrada zaznamenaná, faktúra odoslaná.")}
                data-testid="app-bank-paid" className="px-3 py-1.5 border border-white/20 text-white/75 hover:border-[#D4AF37]/60">Úhrada prijatá na účet</button>
            )}
            {flow.paid && !flow.ok && (
              <button onClick={() => flowAction("verification-success", a.track === "verification" ? "Overenie úspešné? Doklad o preverení online sa pripraví v „Na podpis“." : "Overenie úspešné? Audit sa označí ako potvrdený, ďalej vystavíte certifikát ako doteraz.", "Overenie úspešné – " + (a.track === "verification" ? "doklad čaká v „Na podpis“." : "pokračujte vystavením certifikátu."))}
                data-testid="app-verification-success" className="px-3 py-1.5 bg-[#D4AF37] text-black font-semibold hover:bg-[#F2E3C6]">Overenie úspešné</button>
            )}
            <span className="text-white/45" data-testid="app-flow-state">{!flow.paid ? "Čaká na úhradu" : !flow.ok ? "Prebieha overovanie" : "Overené"}</span>
            {fin.amount > 0 && (
              <span className={fin.paid ? "text-emerald-300" : "text-[#D4AF37]"} data-testid="app-final-fee">
                Záverečný poplatok {Number(fin.amount).toFixed(2).replace(".", ",")} €{fin.promo ? " (akcia −75 %)" : ""} · {fin.paid ? "uhradený" : "čaká na úhradu"}
              </span>
            )}
            {fin.amount > 0 && !fin.paid && (
              <button onClick={() => flowAction("final-bank-paid", `Potvrdiť prijatú úhradu záverečného poplatku ${Number(fin.amount).toFixed(2).replace(".", ",")} € na účet? Vystaví sa faktúra.`, "Záverečný poplatok uhradený, faktúra odoslaná.")}
                data-testid="app-final-bank-paid" className="px-3 py-1.5 border border-white/20 text-white/75 hover:border-[#D4AF37]/60">Záverečný poplatok prijatý na účet</button>
            )}
            {flowMsg && <span className="text-[#D4AF37]">{flowMsg}</span>}
          </div>
        )}
        {a.evidence_number && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#D4AF37]">
            <BadgeCheck size={15} /> {a.evidence_number}
          </div>
        )}

        <div className="flex items-center gap-3 mt-4 text-xs">
          <button onClick={() => setOpen((o) => !o)} data-testid="app-toggle-details"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border transition-colors ${open ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/20 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {open ? "Zavrieť profil" : "Otvoriť profil"}
          </button>
          <span className="text-white/25">{fmtDate(a.created_at)}</span>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 p-5 md:p-6 bg-[#070707] space-y-4">
          {editing ? (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                {EDIT_FIELDS.map(([k, label]) => (
                  <div key={k} className={k === "specialization" || k === "equipment" || k === "certificates" ? "md:col-span-2" : ""}>
                    <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">{label}</label>
                    <input value={draft[k]} onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                      className="w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={save} data-testid="app-save-edit" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors"><Save size={15} /> Uložiť</button>
                <button onClick={() => setEditing(false)} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-white"><X size={15} /> Zrušiť</button>
              </div>
            </>
          ) : (
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Detail label="IČO / Dát. narodenia" value={a.ico_birth} />
              <Detail label="Krajina (fakturácia)" value={a.country} />
              <Detail label="IČ DPH odberateľa" value={a.vat_id} />
              <Detail label="Sídlo / Adresa" value={a.address} icon={MapPin} />
              <Detail label="Zodpovedná osoba" value={a.responsible_person} />
              <Detail label="Počet pracovníkov" value={a.workers_count} />
              <Detail label="Profesia a prax" value={profSummary(a)} full />
              <Detail label="Špecializácia" value={a.specialization} full />
              <Detail label="Technické vybavenie" value={a.equipment} full />
              <Detail label="Certifikáty" value={a.certificates} full />
            </div>
          )}

          <div className="pt-2 border-t border-white/5" data-testid="admin-app-files">
            <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><Paperclip size={12} /> Priložené súbory ({files.length})</p>
            {files.length === 0 ? (
              <p className="text-white/30 text-sm">Žiadne nahraté súbory.</p>
            ) : (
              <ul className="space-y-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      {f.kind === "video" ? <Video size={15} className="text-[#D4AF37] shrink-0" /> : <FileText size={15} className="text-[#D4AF37] shrink-0" />}
                      <span className="truncate text-white/80">{f.original_filename}</span>
                      <span className="text-white/30 text-xs shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    </span>
                    <button onClick={() => viewFile(f.id)} data-testid="admin-file-view" className="text-white/50 hover:text-[#D4AF37] shrink-0" title="Zobraziť"><Eye size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pt-2 border-t border-white/5" data-testid="admin-app-correspondence">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-white/35 text-xs uppercase tracking-wider flex items-center gap-1.5"><MessagesSquare size={12} /> Korešpondencia ({corr.length}) — dôkazový záznam</p>
              <button onClick={() => setComposing(true)} data-testid="correspondence-compose-open"
                className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors shrink-0">
                <Send size={13} /> Napísať e-mail
              </button>
            </div>
            {corr.length === 0 ? (
              <p className="text-white/30 text-sm">Zatiaľ žiadna evidovaná korešpondencia.</p>
            ) : (
              <ul className="space-y-2">
                {corr.map((c) => (
                  <li key={c.id} data-testid="correspondence-item" className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      {c.direction === "in"
                        ? <ArrowDownLeft size={15} className="text-green-400 shrink-0" title="Prijaté" />
                        : <ArrowUpRight size={15} className="text-[#D4AF37] shrink-0" title="Odoslané" />}
                      <span className="truncate text-white/80">{c.subject}</span>
                      <span className="text-white/30 text-xs shrink-0 hidden sm:inline">· {CORR_TYPE_LABEL[c.type] || c.type}</span>
                    </span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="text-white/30 text-xs hidden sm:inline">{fmtDate(c.created_at)}</span>
                      <button onClick={() => setViewing(c)} data-testid="correspondence-view" className="text-white/50 hover:text-[#D4AF37]" title="Zobraziť"><Eye size={16} /></button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pt-2 border-t border-white/5" data-testid="admin-app-letters">
            <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><FileText size={12} /> Personalizované listy — náhľad a odoslanie (auto-doplnené z profilu)</p>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                ["confirmation", "Potvrdenie prijatia"],
                ["notice", "Oznámenie o zaradení"],
                ["completion", "Ukončenie — výzva na úhradu"],
                ["congrats", "Gratulácia"],
              ].map(([type, label]) => (
                <button
                  key={type}
                  data-testid={`app-letter-${type}`}
                  onClick={() => setLetterModal(type)}
                  className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
                >
                  <FileText size={13} /> {label}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-white/5" data-testid="admin-app-audit">
              <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><ClipboardCheck size={12} /> Audítny protokol (predvyplnený subjekt + IČO)</p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={openAuditProtocol}
                  data-testid="app-audit-protocol"
                  className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
                >
                  <Printer size={13} /> Stiahnuť / vytlačiť protokol
                </button>
                <button
                  onClick={confirmAudit}
                  disabled={auditBusy}
                  data-testid="app-confirm-audit"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors disabled:opacity-60 ${
                    auditConfirmed
                      ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-300"
                      : "border border-white/20 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  <CheckCircle2 size={13} /> {auditConfirmed ? "Audit dokončený ✓ (zrušiť)" : "Potvrdiť audit ako dokončený"}
                </button>
              </div>
              {auditConfirmed && a.audit_confirmed_at && (
                <p className="text-emerald-400/70 text-[11px] mt-2" data-testid="app-audit-date">
                  Potvrdené: {String(a.audit_confirmed_at).slice(0, 10)}
                </p>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-white/5" data-testid="admin-app-backup">
              <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><Download size={12} /> Kompletná záloha spisu (ZIP)</p>
              <p className="text-white/40 text-[11px] mb-2">Obsahuje info.json, nahrané súbory, podpísané dokumenty, faktúry, audítny protokol a korešpondenciu — pripravené na externú archiváciu.</p>
              <button
                onClick={downloadBackup}
                data-testid="app-download-backup"
                className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
              >
                <Download size={13} /> Stiahnuť ZIP zálohu
              </button>
            </div>

            <div className="mt-4 pt-3 border-t border-white/5" data-testid="admin-app-hof">
              <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><Trophy size={12} /> Sieň slávy (Tigik) — po gratulácii sa uvedie automaticky</p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={inductHof}
                  disabled={hofBusy || !a.evidence_number}
                  data-testid="app-hof-induct"
                  title={!a.evidence_number ? "Najprv schváľte žiadosť (pridelí evidenčné číslo)" : ""}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${
                    hof?.ok
                      ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-300"
                      : "border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                >
                  {hofBusy ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
                  {hof?.ok ? "Uvedený do Siene slávy ✓ (obnoviť)" : "Uviesť do Siene slávy"}
                </button>
                {hof?.ok && hof.public_url && (
                  <a href={hof.public_url} target="_blank" rel="noreferrer" data-testid="app-hof-link"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/20 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                    <ExternalLink size={13} /> Verejná vizitka
                  </a>
                )}
              </div>
              {hof && !hof.ok && <p className="text-red-400/80 text-[11px] mt-2" data-testid="app-hof-error">{hof.error}</p>}
              {hof?.ok && (
                <div className="mt-3 space-y-1.5" data-testid="hof-status-checklist">
                  <p className="flex items-center gap-2 text-[12px] text-emerald-300">
                    <CheckCircle2 size={14} className="shrink-0" /> Profil v Sieni slávy {hof.action === "updated" ? "aktualizovaný" : "vytvorený"}
                  </p>
                  <p className={`flex items-center gap-2 text-[12px] ${hof.welcome_email_queued ? "text-emerald-300" : "text-white/40"}`}>
                    {hof.welcome_email_queued ? <CheckCircle2 size={14} className="shrink-0" /> : <Clock size={14} className="shrink-0" />}
                    Zlato-biely gratulačný email {hof.welcome_email_queued ? "odoslaný klientovi" : "sa neodoslal (chýbal email)"}
                  </p>
                  {hof.welcome_email_queued && (
                    <p className="flex items-center gap-2 text-[12px] text-emerald-300">
                      <CheckCircle2 size={14} className="shrink-0" /> A4 diplom (PDF) priložený k emailu
                    </p>
                  )}
                </div>
              )}
              {hof?.ok && hof.env === "test" && <p className="text-amber-400/70 text-[11px] mt-2">Sandbox (TEST) — na produkcii sa zapíše do ostrej Siene slávy.</p>}
            </div>

            <div className="mt-4 pt-3 border-t border-white/5" data-testid="admin-app-certified">
              <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><Award size={12} /> Certifikácia — finálne potvrdenie vydania certifikátu</p>
              <button
                onClick={confirmCertified}
                disabled={certBusy}
                data-testid="app-confirm-certified"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors disabled:opacity-60 ${
                  certified
                    ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-300"
                    : "border border-white/20 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {certBusy ? <Loader2 size={13} className="animate-spin" /> : <Award size={13} />}
                {certified ? "Certifikát vydaný ✓ (zrušiť)" : "Potvrdiť certifikát vydaný"}
              </button>
              {certified && a.certified_at && (
                <p className="text-emerald-400/70 text-[11px] mt-2" data-testid="app-certified-date">
                  Vydané: {String(a.certified_at).slice(0, 10)}
                </p>
              )}
              {certMsg && <p className="text-emerald-300/80 text-[11px] mt-1.5" data-testid="app-certified-msg">{certMsg}</p>}
            </div>

            {docs.length > 0 && (
              <div className="mt-3" data-testid="admin-app-sent-docs">
                <p className="text-white/35 text-xs mb-1.5">Odoslané dokumenty (uložené v profile žiadateľa):</p>
                <ul className="space-y-1">
                  {docs.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 text-xs border border-white/10 px-3 py-2">
                      <span className="text-white/70 min-w-0 truncate">{d.title} <span className="text-white/30">· {(d.sent_at || "").slice(0, 10)}</span></span>
                      <div className="flex items-center gap-2 shrink-0">
                        <DeliveryBadge status={d.delivery_status} />
                        <button onClick={() => openPdf(`${API}/applicant/documents/${d.id}/download?token=${dl}`, { filename: `${d.type}.pdf` })}
                          className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline" data-testid={`sent-doc-${d.id}`}>
                          <Download size={12} /> Otvoriť
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-white/5" data-testid="admin-app-signed-docs">
            <p className="text-white/35 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Signature size={12} /> Podpísané uchádzačom — elektronický podpis ({signedDocs.length})
            </p>
            {signedDocs.length === 0 ? (
              <p className="text-white/30 text-sm">Uchádzač zatiaľ nepodpísal žiadny dokument.</p>
            ) : (
              <ul className="space-y-2">
                {signedDocs.map((d) => (
                  <li key={d.id} data-testid={`signed-doc-${d.key}`}
                    className="flex items-center justify-between gap-3 text-sm border border-green-500/20 bg-green-500/5 px-3 py-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 size={15} className="text-green-400 shrink-0" />
                      <span className="text-white/80 truncate">{d.title}</span>
                      <span className="text-white/30 text-xs shrink-0 hidden sm:inline">· podpísané {(d.signed_at || "").slice(0, 10)}</span>
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <button onClick={() => window.open(`${API}/admin/signed-doc/${d.id}/pdf?token=${dl}`, "_blank")}
                        data-testid={`signed-doc-print-${d.key}`}
                        className="inline-flex items-center gap-1 text-white/60 hover:text-[#D4AF37] text-xs" title="Otvoriť / Tlačiť">
                        <Printer size={13} /> Tlačiť
                      </button>
                      <button onClick={() => openPdf(`${API}/admin/signed-doc/${d.id}/pdf?token=${dl}`, { filename: `${d.key}-podpisane-saubereko.pdf` })}
                        data-testid={`signed-doc-download-${d.key}`}
                        className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline text-xs">
                        <Download size={13} /> Stiahnuť
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/5">
            {!approved && (
              <select value={disc} onChange={(e) => setDisc(Number(e.target.value))} data-testid="app-discount-select"
                className="bg-black border border-[#D4AF37]/40 text-[#F2E3C6] px-3 py-2.5 text-sm outline-none">
                <option value={0}>Bez zľavy</option>
                <option value={25}>Zľava 25 %</option>
                <option value={50}>Zľava 50 %</option>
                <option value={75}>Zľava 75 %</option>
              </select>
            )}
            {!approved && (
              <button onClick={() => onApprove(a.id, false, disc)} data-testid="app-approve"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors">
                <CheckCircle2 size={16} /> {disc ? `Schváliť so zľavou ${disc} %` : "Schváliť s registračným poplatkom"}
              </button>
            )}
            {!approved && (
              <button onClick={() => onApprove(a.id, true)} data-testid="app-approve-free"
                className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-sm font-semibold hover:bg-[#D4AF37] hover:text-black transition-colors">
                <CheckCircle2 size={16} /> Schváliť bez poplatku (zdarma)
              </button>
            )}
            {!editing && (
              <button onClick={startEdit} data-testid="app-edit"
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                <Edit3 size={15} /> Upraviť
              </button>
            )}
            <button onClick={() => onRemove(a.id)} data-testid="app-delete"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/50 hover:border-red-400 hover:text-red-400 transition-colors ml-auto">
              <Trash2 size={15} /> Zmazať
            </button>
          </div>
        </div>
      )}
    </div>

    {viewing && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="correspondence-modal" onClick={() => setViewing(null)}>
        <div className="w-full max-w-2xl max-h-[88vh] flex flex-col bg-[#0a0a0a] border border-[#D4AF37]/40" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10">
            <div className="min-w-0">
              <p className="text-white font-medium truncate">{viewing.subject}</p>
              <p className="text-white/40 text-xs mt-0.5">
                {viewing.direction === "in" ? "Prijaté" : "Odoslané"} · {CORR_TYPE_LABEL[viewing.type] || viewing.type} · {fmtDate(viewing.created_at)}
                {viewing.to?.length ? ` · komu: ${viewing.to.join(", ")}` : ""}
              </p>
            </div>
            <button onClick={() => setViewing(null)} data-testid="correspondence-close" className="text-white/50 hover:text-white shrink-0"><X size={20} /></button>
          </div>
          <iframe title="korespondencia" srcDoc={viewing.body_html} className="flex-1 w-full bg-white" style={{ minHeight: "55vh" }} sandbox="" />
        </div>
      </div>
    )}

    {composing && (
      <ComposeModal
        applicant={{ email: a.applicant_email, name: a.subject_name || a.applicant_name, id: a.applicant_id }}
        token={token}
        onClose={() => setComposing(false)}
        onSent={() => { setComposing(false); loadCorr(); }}
      />
    )}

    {letterModal && (
      <SendLetterModal
        type={letterModal}
        app={a}
        token={token}
        onClose={() => setLetterModal(null)}
        onSent={(hofResult) => { setLetterModal(null); loadDocs(); loadCorr(); if (hofResult) setHof(hofResult); }}
      />
    )}
    </>
  );
}

const LETTER_TITLES = {
  confirmation: "Potvrdenie prijatia žiadosti",
  notice: "Oznámenie o zaradení do konania",
  completion: "Úspešné ukončenie certifikácie — výzva na úhradu",
  congrats: "Gratulácia k certifikácii",
};

const DELIVERY_META = {
  sent: { label: "Odoslané", cls: "text-white/50 border-white/20" },
  delivered: { label: "Doručené", cls: "text-green-400 border-green-500/40" },
  opened: { label: "Otvorené", cls: "text-[#D4AF37] border-[#D4AF37]/50" },
  clicked: { label: "Klik", cls: "text-[#D4AF37] border-[#D4AF37]/50" },
  delayed: { label: "Oneskorené", cls: "text-amber-400 border-amber-500/40" },
  bounced: { label: "Vrátené", cls: "text-red-400 border-red-500/40" },
  complained: { label: "Sťažnosť", cls: "text-red-400 border-red-500/40" },
};

function DeliveryBadge({ status }) {
  if (!status) return null;
  const m = DELIVERY_META[status] || { label: status, cls: "text-white/50 border-white/20" };
  return <span className={`px-2 py-0.5 border text-[10px] uppercase tracking-wide ${m.cls}`} data-testid={`delivery-${status}`}>{m.label}</span>;
}

function SendLetterModal({ type, app, token, onClose, onSent }) {
  const dl = useDl(token);
  const [form, setForm] = useState({
    name: app.subject_name || app.applicant_name || "",
    evidence: app.evidence_number || "",
    address: app.address || "",
    email: app.applicant_email || "",
  });
  const [lang, setLang] = useState(app.language || "sk");
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setShowPreview(false); };

  const previewUrl = `${API}/admin/letter-preview/${type}?token=${dl}&lang=${lang}`
    + `&name=${encodeURIComponent(form.name)}&evidence=${encodeURIComponent(form.evidence)}&address=${encodeURIComponent(form.address)}`;

  const send = async () => {
    if (!form.email.trim()) { setError("Zadajte e-mail príjemcu."); return; }
    setSending(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/applications/${app.id}/send-letter`,
        { type, name: form.name.trim(), evidence: form.evidence.trim(), address: form.address.trim(), email: form.email.trim(), lang },
        { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
      setTimeout(() => onSent(data.hof), 900);
    } catch (err) {
      setError(err.response?.data?.detail || "Odoslanie zlyhalo.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="send-letter-modal">
      <div className="w-full max-w-3xl max-h-[92vh] flex flex-col bg-[#0a0a0a] border border-[#D4AF37]/40">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]">Odoslať list žiadateľovi</p>
            <h3 className="font-display text-lg text-white mt-0.5">{LETTER_TITLES[type]}</h3>
          </div>
          <button onClick={onClose} data-testid="send-letter-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <LField label="E-mail príjemcu *" value={form.email} onChange={set("email")} testid="sl-email" type="email" />
            <LField label="Meno / Názov" value={form.name} onChange={set("name")} testid="sl-name" />
            <LField label="Evidenčné číslo" value={form.evidence} onChange={set("evidence")} testid="sl-evidence" />
            <LField label="Sídlo / Adresa" value={form.address} onChange={set("address")} testid="sl-address" />
            <label className="block">
              <span className="block text-xs text-white/50 mb-1">Jazyk dokumentu</span>
              <select value={lang} onChange={(e) => { setLang(e.target.value); setShowPreview(false); }} data-testid="sl-lang"
                className="w-full bg-[#111] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]">
                <option value="sk">Slovensky 🇸🇰</option>
                <option value="de">Deutsch 🇩🇪</option>
                <option value="en">English 🇬🇧</option>
              </select>
            </label>
            <button onClick={() => setShowPreview(true)} data-testid="sl-preview-btn"
              className="w-full inline-flex items-center justify-center gap-2 border border-white/15 py-2.5 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <FileText size={15} /> Zobraziť náhľad
            </button>
            {error && <p className="text-red-400 text-sm" data-testid="sl-error">{error}</p>}
            <button onClick={send} disabled={sending || sent} data-testid="sl-send"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold py-3 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
              {sent ? <><CheckCircle2 size={16} /> Odoslané</> : sending ? <><Loader2 size={16} className="animate-spin" /> Odosielam…</> : <><Send size={16} /> Odoslať e-mailom</>}
            </button>
          </div>
          <div className="border border-white/10 bg-white/5 min-h-[300px] flex items-center justify-center">
            {showPreview ? (
              <iframe title="náhľad listu" src={previewUrl} className="w-full h-full bg-white" style={{ minHeight: "60vh" }} data-testid="sl-preview-frame" />
            ) : (
              <p className="text-white/30 text-sm text-center px-6">Náhľad sa zobrazí po kliknutí na „Zobraziť náhľad". Skontrolujte údaje pred odoslaním.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LField({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs text-white/50 mb-1">{label}</span>
      <input {...props} data-testid={testid}
        className="w-full bg-[#111] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37] transition-colors" />
    </label>
  );
}

function ComposeModal({ applicant, token, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [lang, setLang] = useState(pickEmailLang(applicant.language));
  const [previewHtml, setPreviewHtml] = useState(null);
  const preview = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/correspondence/preview`,
        { email: applicant.email, applicant_id: applicant.id, subject, message, language: lang || null,
          attachments: files.map((f) => ({ filename: f.filename, content: "" })) },
        { headers: { Authorization: `Bearer ${token}` } });
      setPreviewHtml(data.html);
    } catch { setError("Náhľad sa nepodarilo zobraziť."); }
  };

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    arr.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = String(reader.result).split(",")[1] || "";
        setFiles((prev) => [...prev, { filename: f.name, content: base64, content_type: f.type, size: f.size }]);
      };
      reader.readAsDataURL(f);
    });
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
  const fmtSize = (b) => (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");

  const send = async () => {
    if (!subject.trim() || !message.trim()) { setError("Vyplňte predmet aj správu."); return; }
    if (totalSize > 15 * 1024 * 1024) { setError("Prílohy sú príliš veľké (max 15 MB spolu)."); return; }
    setError(""); setSending(true);
    try {
      await axios.post(`${API}/admin/correspondence/send`,
        { email: applicant.email, applicant_id: applicant.id, subject, message, language: lang || null,
          attachments: files.map((f) => ({ filename: f.filename, content: f.content, content_type: f.content_type })) },
        { headers: { Authorization: `Bearer ${token}` } });
      onSent();
    } catch (e) {
      setError(e.response?.data?.detail || "E-mail sa nepodarilo odoslať.");
    } finally { setSending(false); }
  };

  return (
    <>
    {previewHtml !== null && (
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 sm:px-4" data-testid="compose-preview-modal" onClick={() => setPreviewHtml(null)}>
        <div className="w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-[#0a0a0a] sm:border border-white/15 flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <span className="text-white/70 text-sm">Náhľad e-mailu</span>
            <button onClick={() => setPreviewHtml(null)} data-testid="compose-preview-close" className="text-white/50 hover:text-white"><X size={18} /></button>
          </div>
          <iframe title="Náhľad e-mailu" sandbox="" srcDoc={previewDoc(previewHtml)} className="w-full flex-1 min-h-0 sm:flex-none sm:h-[70vh] bg-white" style={{ border: 0 }} />
        </div>
      </div>
    )}
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="compose-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-[#D4AF37]/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-white font-medium">Napísať e-mail žiadateľovi</p>
            <p className="text-white/40 text-xs mt-0.5 truncate">komu: {applicant.email}</p>
          </div>
          <button onClick={onClose} data-testid="compose-close" className="text-white/50 hover:text-white shrink-0"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <EmailLangPicker value={lang} onChange={setLang} testid="compose-lang" />
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Predmet</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject"
              placeholder="Napr. Žiadosť o doplnenie podkladov"
              className="w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Správa</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="compose-message" rows={7}
              placeholder="Dobrý deň, prosíme Vás o doloženie..."
              className="w-full bg-[#111] border border-white/15 p-3 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="compose-error">{error}</p>}

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Prílohy (obrázok, PDF, kópia auditu…)</label>
            <label data-testid="compose-attach-btn" className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-sm text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer">
              <Paperclip size={15} /> Pripojiť súbor
              <input type="file" multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                data-testid="compose-file-input"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic" className="hidden" />
            </label>
            {files.length > 0 && (
              <div className="mt-2.5 space-y-1.5" data-testid="compose-attach-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 bg-[#111] border border-white/10 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0 text-white/75">
                      <Paperclip size={13} className="text-[#D4AF37] shrink-0" />
                      <span className="truncate">{f.filename}</span>
                      <span className="text-white/35 shrink-0">· {fmtSize(f.size)}</span>
                    </span>
                    <button onClick={() => removeFile(i)} data-testid={`compose-remove-file-${i}`} className="text-white/40 hover:text-red-400 shrink-0"><X size={15} /></button>
                  </div>
                ))}
                <p className="text-white/30 text-[11px]">Spolu: {fmtSize(totalSize)} / max 15 MB</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3">
            <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-white">Zrušiť</button>
            <button onClick={preview} data-testid="compose-preview"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors"><Eye size={15} /> Náhľad</button>
            <button onClick={send} disabled={sending} data-testid="compose-send"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Odosielam..." : "Odoslať a uložiť"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function Detail({ label, value, full, icon: Icon }) {
  if (!value) return null;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-white/35 text-xs uppercase tracking-wider mb-0.5 flex items-center gap-1.5">{Icon && <Icon size={12} />}{label}</p>
      <p className="text-white/75 whitespace-pre-wrap">{value}</p>
    </div>
  );
}
